import { BaseAnalyticsProvider } from '../../core/base-analytics.provider';
import { MoEngageConfig } from './moengage.config';
import moengage from '@moengage/web-sdk';

export class MoEngageProvider extends BaseAnalyticsProvider {
  readonly name = 'MoEngage';
  readonly providerType = 'moengage';
  readonly version = '1.0.0';
  
  private moengageInstance: any = null;

  constructor(config: MoEngageConfig) {
    super(config);
  }

  async initialize(): Promise<void> {
    try {
      if (!this._config.appId) {
        throw new Error('MoEngage App ID is required');
      }

      if (!this._config.cluster) {
        throw new Error('MoEngage cluster is required');
      }

      // Check domain and set debug logs accordingly
      const currentDomain = window.location.hostname;
      const debugLogs = currentDomain === 'app.shiprocket.in' ? 0 : 1;

      // Initialize MoEngage using the npm package
      moengage.initialize({
        app_id: this._config.appId,
        cluster: this._config.cluster,
        debug_logs: debugLogs,
        enableSPA: this._config.enableSPA,
        swPath: this._config.swPath
      });

      this.moengageInstance = moengage;
      this.setInitialized(true);
      this.setLastError(null);
      
      this.logInfo('MoEngage provider initialized successfully');
    } catch (error) {
      this.setLastError(error instanceof Error ? error : new Error(String(error)));
      this.setInitialized(false);
      throw error;
    }
  }

  async destroy(): Promise<void> {
    try {
      if (this.moengageInstance) {
        // Call MoEngage's destroy_session to clean up the current session
        try {
          this.moengageInstance.destroy_session();
          this.logInfo('MoEngage session destroyed successfully');
        } catch (sessionError) {
          this.logWarning('Error destroying MoEngage session', sessionError);
        }
        
        // Clear the instance reference
        this.moengageInstance = null;
      }
      this.setInitialized(false);
      this.logInfo('MoEngage provider destroyed');
    } catch (error) {
      this.logError('Error destroying MoEngage provider', error);
    }
  }

  /**
   * Destroy the current MoEngage session
   * This is useful when users log out or when you need to reset session state
   */
  async destroySession(): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      if (this.moengageInstance && typeof this.moengageInstance.destroy_session === 'function') {
        this.moengageInstance.destroy_session();
        this.logInfo('MoEngage session destroyed successfully');
      } else {
        this.logWarning('MoEngage destroy_session method not available');
      }
    } catch (error) {
      this.logError('Error destroying MoEngage session', error);
      throw error;
    }
  }

  /**
   * Add first name to MoEngage user profile
   */
  addFirstName(firstName: string): void {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      if (this.moengageInstance && typeof this.moengageInstance.add_first_name === 'function') {
        this.moengageInstance.add_first_name(firstName);
        this.logInfo(`First name added: ${firstName}`);
      } else {
        this.logWarning('MoEngage add_first_name method not available');
      }
    } catch (error) {
      this.logError('Error adding first name to MoEngage', error);
    }
  }

  /**
   * Add last name to MoEngage user profile
   */
  addLastName(lastName: string): void {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      if (this.moengageInstance && typeof this.moengageInstance.add_last_name === 'function') {
        this.moengageInstance.add_last_name(lastName);
        this.logInfo(`Last name added: ${lastName}`);
      } else {
        this.logWarning('MoEngage add_last_name method not available');
      }
    } catch (error) {
      this.logError('Error adding last name to MoEngage', error);
    }
  }

  /**
   * Add email to MoEngage user profile
   */
  addEmail(email: string): void {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      if (this.moengageInstance && typeof this.moengageInstance.add_email === 'function') {
        this.moengageInstance.add_email(email);
        this.logInfo(`Email added: ${email}`);
      } else {
        this.logWarning('MoEngage add_email method not available');
      }
    } catch (error) {
      this.logError('Error adding email to MoEngage', error);
    }
  }

  /**
   * Add mobile number to MoEngage user profile
   */
  addMobile(mobile: string): void {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      if (this.moengageInstance && typeof this.moengageInstance.add_mobile === 'function') {
        this.moengageInstance.add_mobile(mobile);
        this.logInfo(`Mobile added: ${mobile}`);
      } else {
        this.logWarning('MoEngage add_mobile method not available');
      }
    } catch (error) {
      this.logError('Error adding mobile to MoEngage', error);
    }
  }

  /**
   * Set multiple user attributes at once
   */
  setUserAttributes(attributes: {
    firstName?: string;
    lastName?: string;
    email?: string;
    mobile?: string;
  }): void {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      if (attributes.firstName) {
        this.addFirstName(attributes.firstName);
      }
      if (attributes.lastName) {
        this.addLastName(attributes.lastName);
      }
      if (attributes.email) {
        this.addEmail(attributes.email);
      }
      if (attributes.mobile) {
        this.addMobile(attributes.mobile);
      }
      
      this.logInfo('User attributes set successfully', attributes);
    } catch (error) {
      this.logError('Error setting user attributes in MoEngage', error);
    }
  }

  async trackEvent(eventName: string, properties?: Record<string, any>): Promise<void> {
    console.log(`[MoEngageProvider] 🔍 trackEvent called for: "${eventName}"`);
    console.log(`[MoEngageProvider] 📊 Properties:`, properties);
    console.log(`[MoEngageProvider] 📋 Provider status - enabled: ${this.isEnabled}, initialized: ${this.isInitialized}`);
    
    if (!this.isEnabled || !this.isInitialized) {
      console.warn(`[MoEngageProvider] ⚠️ Provider not ready - enabled: ${this.isEnabled}, initialized: ${this.isInitialized}`);
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      const eventProperties = {
        ...properties,
        bu_name: 'Domestic'
      };

      console.log(`[MoEngageProvider] 📤 Sending to MoEngage SDK:`, eventProperties);
      console.log(`[MoEngageProvider] 🔧 MoEngage instance available:`, !!this.moengageInstance);
      
      this.moengageInstance.track_event(eventName, eventProperties);
      console.log(`[MoEngageProvider] ✅ Event sent successfully to MoEngage SDK`);
      this.logInfo(`Event tracked: ${eventName}`, eventProperties);
    } catch (error) {
      console.error(`[MoEngageProvider] ❌ Error tracking event:`, error);
      this.logError(`Error tracking event: ${eventName}`, error);
      throw error;
    }
  }

  async identifyUser(userId: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      this.moengageInstance.identifyUser(userId);
      
      // MoEngage doesn't have a direct setUserProperties method
      // We need to use their specific API for user properties
      if (properties && Object.keys(properties).length > 0) {
        this.logInfo(`User identified: ${userId}`, properties);
        // Note: MoEngage user properties need to be set differently
        // This is a limitation of their API
      } else {
        this.logInfo(`User identified: ${userId}`);
      }
    } catch (error) {
      this.logError(`Error identifying user: ${userId}`, error);
      throw error;
    }
  }

  async setUserProperties(properties: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      // MoEngage specific implementation for user properties
      // This might need to be implemented based on MoEngage's API
      this.logInfo('MoEngage setUserProperties called', properties);
      // Note: Implementation depends on MoEngage's specific user property API
    } catch (error) {
      this.logError('Error setting user properties in MoEngage', error);
      throw error;
    }
  }

  async trackPageView(properties?: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('MoEngage provider not enabled or not initialized');
      return;
    }

    try {
      // MoEngage automatically tracks page views with enableSPA: true
      // You can add custom page view tracking if needed
      this.logInfo('MoEngage page view tracked (auto-tracked)', properties);
    } catch (error) {
      this.logError('Error tracking page view in MoEngage', error);
      throw error;
    }
  }

  validateConfig(config: any): boolean {
    return config && 
           typeof config === 'object' && 
           typeof config.appId === 'string' && 
           config.appId.length > 0 &&
           typeof config.cluster === 'string' && 
           config.cluster.length > 0;
  }

  getProviderInfo(): Record<string, any> {
    return {
      ...super.getProviderInfo(),
      moengageInstance: !!this.moengageInstance,
      config: {
        appId: this._config.appId ? `${this._config.appId.substring(0, 8)}...` : 'not set',
        cluster: this._config.cluster,
        enableSPA: this._config.enableSPA,
        debugLogs: this._config.debugLogs
      }
    };
  }
} 